/**
 * @fileoverview Contexto responsável por gerenciar conexões Socket.IO reutilizáveis e seguras por empresa e usuário.
 */

import { createContext } from "react";
import openSocket from "socket.io-client";
import { jwtDecode } from "jwt-decode";

class ManagedSocket {
  constructor(socketManager) {
    this.socketManager = socketManager;
    this.rawSocket = socketManager.currentSocket;
    this.callbacks = [];
    this.joins = [];
    this.connectHandler = null;
    this.refreshJoinsOnReady = null;
  }

  // O socket bruto é compartilhado pela aba (só é trocado quando muda a empresa
  // ou o usuário). O ouvinte de "connect", que restaura ouvintes e salas desta
  // instância na reconexão, só existe enquanto ela tem o que restaurar: quem só
  // emite (o "digitando…" chama getSocket a cada aviso) não deixa nada para
  // trás, nem quem só ouve e sai com off(). Quem entrou numa sala segue
  // registrado até disconnect().
  registrarReconexao() {
    if (!this.rawSocket || this.connectHandler) return;

    this.connectHandler = () => {
      if (this.rawSocket.recovered) return;
      const socket = this.rawSocket;
      // Um reenvio por conexão: o de uma conexão que caiu antes do "ready" sai.
      if (this.refreshJoinsOnReady) socket.off("ready", this.refreshJoinsOnReady);
      // Tira a si mesma (e não o que estiver no campo): uma função órfã que
      // lançasse erro no "ready" impedia os ouvintes seguintes, e o chat aberto
      // parava de receber até um F5.
      const reenviar = () => {
        socket.off("ready", reenviar);
        if (this.refreshJoinsOnReady === reenviar) this.refreshJoinsOnReady = null;
        if (this.rawSocket !== socket) return;
        // Só as salas que esta conexão ainda não recebeu: o servidor conta as
        // entradas, e uma a mais deixaria a aba na sala depois do leave.
        for (const j of this.joins) {
          if (j.entregueNa === this.socketManager.conexao) continue;
          j.entregueNa = this.socketManager.conexao;
          socket.emit(`join${j.event}`, ...j.params);
        }
      };
      this.refreshJoinsOnReady = reenviar;
      for (const j of this.callbacks) {
        socket.off(j.event, j.callback);
        socket.on(j.event, j.callback);
      }
      socket.on("ready", reenviar);
    };

    this.rawSocket.on("connect", this.connectHandler);

    // Entre o "connect" e o "ready" o servidor ainda não ouve os joins (descarta
    // o que chega) e o "connect" desta conexão já passou: arma agora o reenvio
    // no "ready", como o "connect" teria armado.
    if (this.rawSocket.connected && !this.socketManager.conexaoPronta) {
      this.connectHandler();
    }
  }

  liberarReconexaoSeVazia() {
    if (!this.rawSocket || !this.connectHandler) return;
    if (this.callbacks.length > 0 || this.joins.length > 0) return;
    if (this.refreshJoinsOnReady) {
      this.rawSocket.off("ready", this.refreshJoinsOnReady);
      this.refreshJoinsOnReady = null;
    }
    this.rawSocket.off("connect", this.connectHandler);
    this.connectHandler = null;
  }

  on(event, callback) {
    if (!this.rawSocket) return;
    // "ready" (e "connect", que as telas usam no mesmo sentido): a cada conexão
    // pronta, a primeira e cada volta depois de uma queda, quem ouve
    // ressincroniza. Já pronta agora, roda na hora.
    if (event === "ready" || event === "connect") {
      this.callbacks.push({ event: "ready", callback });
      this.registrarReconexao();
      this.rawSocket.on("ready", callback);
      if (this.socketManager.conexaoPronta) callback();
      return;
    }
    this.callbacks.push({event, callback});
    this.registrarReconexao();
    return this.rawSocket.on(event, callback);
  }

  off(event, callback) {
    if (!this.rawSocket) return;
    if (event === "connect") event = "ready";

    if (!callback) {
      const eventCallbacks = this.callbacks.filter((c) => c.event === event);
      for (const c of eventCallbacks) {
        this.rawSocket.off(c.event, c.callback);
      }
      this.callbacks = this.callbacks.filter((c) => c.event !== event);
      this.liberarReconexaoSeVazia();
      return;
    }

    const i = this.callbacks.findIndex((c) => c.event === event && c.callback === callback);
    if (i !== -1) {
      this.callbacks.splice(i, 1);
      this.liberarReconexaoSeVazia();
    }
    return this.rawSocket.off(event, callback);
  }

  emit(event, ...params) {
    if (!this.rawSocket) return;
    if (event.startsWith("join")) {
      const join = this.lembrarJoin(event.substring(4), params);
      this.registrarReconexao();
      // Já entrou nesta sala nesta conexão: não entra de novo (o servidor conta
      // as entradas e só tira a aba da sala quando a conta zera).
      if (join.entregueNa === this.socketManager.conexao) return;
      // Antes do "ready" o servidor descarta o join: o reenvio no "ready" entrega.
      if (this.socketManager.conexaoPronta) join.entregueNa = this.socketManager.conexao;
    } else if (event.startsWith("leave")) {
      // Saiu da sala: ela não volta na próxima reconexão.
      this.esquecerJoin(event.substring(5), params);
    }
    return this.rawSocket.emit(event, ...params);
  }

  esquecerJoin(event, params) {
    const chave = JSON.stringify(params);
    this.joins = this.joins.filter((j) => !(j.event === event && j.chave === chave));
    this.liberarReconexaoSeVazia();
  }

  // Uma entrada por sala (evento e parâmetros iguais), com a conexão em que o
  // servidor a recebeu.
  lembrarJoin(event, params) {
    const chave = JSON.stringify(params);
    let join = this.joins.find((j) => j.event === event && j.chave === chave);
    if (!join) {
      join = { event, params, chave, entregueNa: null };
      this.joins.push(join);
    }
    return join;
  }

  disconnect() {
    if (!this.rawSocket) return;

    if (this.refreshJoinsOnReady) {
      this.rawSocket.off("ready", this.refreshJoinsOnReady);
      this.refreshJoinsOnReady = null;
    }

    if (this.connectHandler) {
      this.rawSocket.off("connect", this.connectHandler);
      this.connectHandler = null;
    }

    for (const j of this.joins) {
      this.rawSocket.emit(`leave${j.event}`, ...j.params);
    }
    this.joins = [];

    for (const c of this.callbacks) {
      this.rawSocket.off(c.event, c.callback);
    }
    this.callbacks = [];
    this.rawSocket = null;
  }
}

class DummySocket {
  on(..._) {}
  off(..._) {}
  emit(..._) {}
  disconnect() {}
}

// Claims de um JWT (null se não der para ler).
const lerToken = (token) => {
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

// Mesmo usuário na mesma empresa (troca de empresa abre outra conexão).
const mesmaSessao = (a, b) => {
  const x = lerToken(a);
  const y = lerToken(b);
  return !!x && !!y && String(x.id) === String(y.id) && String(x.companyId) === String(y.companyId);
};

const SocketManager = {
  currentCompanyId: -1,
  currentUserId: -1,
  currentSocket: null,
  currentToken: null,
  renewTimer: null,
  // Se a conexão atual já recebeu o "ready".
  conexaoPronta: false,
  // Conta as conexões da aba: cada sala lembra em qual delas o servidor a recebeu.
  conexao: 0,

  getSocket: function(companyId) {
    let userId = null;
    if (localStorage.getItem("userId")) {
      userId = localStorage.getItem("userId");
    }

    if (!companyId && !this.currentSocket) {
      return new DummySocket();
    }

    if (companyId && typeof companyId !== "string") {
      companyId = `${companyId}`;
    }

    if (companyId !== this.currentCompanyId || userId !== this.currentUserId) {
      this.fechar();

      let token = localStorage.getItem("token");
      if (!token || typeof token !== 'string') {
        return new DummySocket();
      }
      
      let exp;
      try {
        const decoded = jwtDecode(token);
        exp = decoded.exp;
        if (!exp) {
          return new DummySocket();
        }
      } catch (error) {
        console.warn("Token inválido:", error);
        return new DummySocket();
      }

      if ( Date.now() >= exp*1000) {
        if (process.env.NODE_ENV !== 'production') {
          // console.warn("Expired token, reload after refresh");
        }
        setTimeout(() => {
          window.location.reload();
        },1000);
        return new DummySocket();
      }

      this.currentCompanyId = companyId;
      this.currentUserId = userId;
      
      if (!token) {
        return new DummySocket();
      }
      
      const socket = openSocket(process.env.REACT_APP_BACKEND_URL, {
        transports: ["websocket", "polling"], // WebSocket first, polling fallback
        upgrade: true,                         // Enable automatic upgrade
        pingTimeout: 120000,                   // Sync with backend (120s)
        pingInterval: 30000,                   // Sync with backend (30s)
        query: { token },
      });
      this.currentSocket = socket;
      this.currentToken = token;

      // Registrados antes de qualquer ManagedSocket: quando o "connect" e o
      // "ready" chegam a eles, conexaoPronta já vale para a conexão nova.
      this.conexaoPronta = false;
      socket.on("connect", () => {
        this.conexaoPronta = false;
        this.conexao += 1;
      });
      socket.on("ready", () => {
        this.conexaoPronta = true;
      });
      // O servidor avisa que o acesso desta conexão acabou ou mudou (sessão
      // encerrada, token vencido, pessoa tirada da empresa): o useAuth relê a
      // sessão e, se ela não vale mais, leva ao login.
      socket.on("auth_error", () => window.dispatchEvent(new Event("auth:error")));

      // Token renovado (a cada 23 h, ao voltar para a aba ou num 401): a
      // conexão aberta passa a valer até o novo vencimento (o servidor corta
      // quando vence o token com que ela abriu) e as reconexões já vão com ele.
      const usarTokenNovo = () => {
        const novo = localStorage.getItem("token");
        if (this.currentSocket !== socket || !novo || novo === this.currentToken) return;
        if (!mesmaSessao(novo, this.currentToken)) return;
        this.currentToken = novo;
        socket.io.opts.query = { ...(socket.io.opts.query || {}), token: novo };
        if (socket.connected) socket.emit("renewToken", novo);
      };
      this.renewTimer = setInterval(usarTokenNovo, 60 * 1000);

      // Corte pelo servidor (token vencido, permissões mudadas, empresa
      // reativada): reconecta com o token mais novo, com pausa que cresce a
      // cada corte seguido. Queda de rede o próprio socket.io reconecta.
      let cortesSeguidos = 0;
      socket.on("ready", () => {
        cortesSeguidos = 0;
      });
      socket.on("disconnect", (reason) => {
        if (!reason.startsWith("io ")) return;
        const exp = lerToken(localStorage.getItem("token"))?.exp;
        if (!exp) return;
        if (Date.now() >= exp * 1000) {
          // O token da aba também venceu: recarregar renova o login.
          window.location.reload();
          return;
        }
        usarTokenNovo();
        const pausa = Math.min(30000, 500 * 2 ** cortesSeguidos);
        cortesSeguidos += 1;
        setTimeout(() => {
          if (this.currentSocket === socket && !socket.connected) socket.connect();
        }, pausa);
      });

      // Muta logs de eventos em produção/dev para evitar vazamento de dados sensíveis
      this.currentSocket.onAny(() => {});
    }
    
    const managed = new ManagedSocket(this);
    // expõe para casos onde precisamos emitir fora de hooks/contexts
    if (typeof window !== 'undefined') {
      window.currentSocketManager = this;
    }
    return managed;
  },

  // Fecha a conexão da aba (troca de empresa ou de usuário, sessão encerrada).
  // Sem isto, entrar de novo na mesma aba, com o mesmo usuário e empresa,
  // reaproveitava a conexão que o servidor derrubou ao encerrar a sessão.
  fechar: function() {
    if (!this.currentSocket) return;
    clearInterval(this.renewTimer);
    this.currentSocket.removeAllListeners();
    this.currentSocket.disconnect();
    this.currentSocket = null;
    this.currentToken = null;
    this.currentCompanyId = -1;
    this.currentUserId = -1;
  },
};

const SocketContext = createContext()

export { SocketContext, SocketManager };
